import { ActionRequest } from '../types'
import { LoadingButton, LoadingButtonProps } from '@mui/lab'
import { updateRequestState } from '../services/requestService'
import { RequestableType } from '../enums/RequestableType'
import { useDispatch } from 'react-redux'
import { createSuccessNotification, createWarningNotification } from '../redux/slices/notifications'
import { deleteCompanyRequest, deleteProjectRequest } from '../redux/slices/requests'
import { createUserNotification } from '../utils/createUserNotification'
import { RequestAccessErrors } from '../enums/RequestAccessErrors'
import useAuth from '../hooks/useAuth'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { RequestStatus } from '../enums/RequestStatus'

export interface ActionRequestRejectButtonProps extends Omit<
  LoadingButtonProps,
  'children' | 'onClick' | 'loading'
> {
  actionRequest: ActionRequest
  label?: string
  afterSubmit?: (actionRequest: ActionRequest) => void
}

export default function ActionRequestRejectButton({
  actionRequest,
  afterSubmit,
  label,
  ...props
}: ActionRequestRejectButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * Indicates if the action request is being updated.
   */
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  /**
   * Handle the "reject" action.
   */
  const handleReject = async () => {
    try {
      setIsSubmitting(true)

      const updatedActionRequest = await updateRequestState(actionRequest.id, {
        status: RequestStatus.REJECTED
      })

      switch (actionRequest.requestableType) {
        case RequestableType.COMPANY:
          dispatch(deleteCompanyRequest(actionRequest.id))
          break
        case RequestableType.PROJECT:
          dispatch(deleteProjectRequest(actionRequest.id))
          break
        default:
          break
      }

      dispatch(createSuccessNotification(t('requestRejectSuccess')))

      afterSubmit?.(updatedActionRequest)
    } catch (error) {
      const errorMessage = createUserNotification({
        user: currentUser,
        type: RequestAccessErrors.DELETE,
        error: error
      })

      dispatch(createWarningNotification(t(errorMessage.key) + t(errorMessage.message)))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <LoadingButton
      variant="text"
      color="error"
      {...props}
      loading={isSubmitting}
      onClick={handleReject}
    >
      {label ?? t('reject')}
    </LoadingButton>
  )
}
